<script setup>
import { ref, onMounted, watch, nextTick, onBeforeUnmount, getCurrentInstance } from 'vue'
import api from '@/services/api'
import DatePicker from '@/components/DatePicker.vue'
// ✅ Import actualizado - GenderSelect movido a MedicalRecord/Forms/Common
import GenderSelect from '@/components/MedicalRecord/Forms/Common/GenderSelect.vue'
import Swal from 'sweetalert2';
const { proxy } = getCurrentInstance();

const props = defineProps({
    label: {
        type: String,
        required: true
    },
    modelValue: {
        type: [String, Number],
        required: false,
        default: ''
    },
    apiurl: {
        type: String,
        required: false,
        default: 'pacientes'
    },
    canEditAndAdd: {
        type: Boolean,
        required: true
    },
    localData: {
        type: Array,
        required: false,
        default: () => []
    },
    errors: {
        type: Object,
        default: () => ({})
    },
    errorFieldName: {
        type: String,
        default: ''
    }
})

const emit = defineEmits(['update:modelValue'])

const allMenuItems = ref([])
const menuItems = ref([])
const newItemFirstName = ref('')
const newItemLastName = ref('')
const newItemGender = ref('')
const newItemWhatsapp = ref('')
const newItemBirthdate = ref('')
const selectedItemId = ref(props.modelValue || '')
const selectedItemName = ref('')
const searchQuery = ref('')
const dropdown = ref(null)
const dropdownUp = ref(false)
const dropdownLeft = ref(false)
const dropdownRight = ref(false)
const dropdownWidth = ref('400px')
const isEditing = ref(false)
const editingItemId = ref(null)
const isDropdownOpen = ref(false)
const isLoadingMore = ref(false)
const currentPage = ref(1)
const itemsPerPage = ref(100)
const totalPages = ref(1)
const scrollableOptions = ref(null)

const handleClickOutside = (event) => {
    if (!dropdown.value || dropdown.value.contains(event.target)) return;

    const datepickerElements = document.querySelectorAll('.flatpickr-calendar');
    for (let el of datepickerElements) {
        if (el.contains(event.target)) return;
    }
};

const normalizeString = (str) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()

const normalizeData = (data) => {
    return data.map(item => ({
        id: item.id,
        first_name: item.first_name,
        last_name: item.last_name,
        birthdate: item.birthdate,
        gender: item.gender,
        whatsapp: item.whatsapp,
        name: `${item.first_name} ${item.last_name}`,
        normalizedName: normalizeString(`${item.first_name} ${item.last_name}`),
        deleted_at: item.deleted_at || null,
    }));
}

const fetchAllMenuValues = async (page = 1) => {
    if (allMenuItems.value.length > 0) {
        updateMenuItems();
        return;
    }

    try {
        isLoadingMore.value = true;
        let url = `/${props.apiurl}?page=${page}&per_page=${itemsPerPage.value}`;
        if (searchQuery.value) url += `&search=${normalizeString(searchQuery.value)}`;

        const response = await api.get(url);
        allMenuItems.value = normalizeData(response.data.data);
        totalPages.value = response.data.pagination.last_page;
        updateMenuItems();
    } catch (error) {
        console.error('Error fetching menu values:', error);
    } finally {
        isLoadingMore.value = false;
    }
};

const updateMenuItems = async () => {
    const filteredItems = allMenuItems.value.filter(item => {
        const notDeleted = item.deleted_at === null || item.deleted_at === undefined;
        return notDeleted && item.normalizedName.includes(normalizeString(searchQuery.value));
    });

    menuItems.value = filteredItems;

    if (selectedItemId.value) {
        let selectedItem = allMenuItems.value.find(item => String(item.id) === String(selectedItemId.value));

        if (!selectedItem || selectedItem.deleted_at) {
            try {
                const selectedItemResponse = await api.get(`/${props.apiurl}/${selectedItemId.value}`);
                const selectedItemData = normalizeData([selectedItemResponse.data.data]);
                allMenuItems.value.push(...selectedItemData);
            } catch (error) {
                console.error("Error fetching selected item:", error);
            }
        }
    }
};

const findSelectedItem = async (id) => {
    if (!id) {
        selectedItemName.value = '';
        return;
    }

    // Buscar primero en los datos locales
    let selectedItem = allMenuItems.value.find(item => String(item.id) === String(id));

    if (selectedItem) {
        selectedItemName.value = `${selectedItem.last_name} ${selectedItem.first_name}`;
        return; // Si lo encuentra en los datos locales, no es necesario hacer la petición a la API
    }

    // Si no está en los datos locales, hacer la petición a la API
    try {
        const response = await api.get(`/${props.apiurl}/${id}`);
        const selectedItemData = normalizeData([response.data.data])[0];
        allMenuItems.value.push(selectedItemData);
        selectedItemName.value = `${selectedItemData.last_name} ${selectedItemData.first_name}`;
    } catch (error) {
        console.error('Error fetching selected item:', error);
    }
};

const selectItem = (item) => {
    selectedItemId.value = item.id;
    selectedItemName.value = `${item.first_name} ${item.last_name}`;
    emit('update:modelValue', item.id);
    closeDropdown();
    updateMenuItems();
};

const editItem = (item) => {
    newItemFirstName.value = item.first_name;
    newItemLastName.value = item.last_name;
    newItemGender.value = item.gender;
    newItemWhatsapp.value = item.whatsapp;
    newItemBirthdate.value = item.birthdate;
    isEditing.value = true;
    editingItemId.value = item.id;
}

const updateItem = async () => {
    if (!newItemFirstName.value.trim() || !newItemLastName.value.trim()) return;
    try {
        const response = await api.put(`/${props.apiurl}/${editingItemId.value}`, {
            first_name: newItemFirstName.value,
            last_name: newItemLastName.value,
            gender: newItemGender.value,
            whatsapp: newItemWhatsapp.value,
            birthdate: newItemBirthdate.value
        });
        const updatedItem = response.data.data;

        proxy.$notify(response.data.message, "", "success", "ni ni-like-2 ni-lg");

        const index = allMenuItems.value.findIndex(item => item.id === editingItemId.value);
        if (index !== -1) {
            allMenuItems.value[index] = normalizeData([updatedItem])[0];
        }

        if (selectedItemId.value === editingItemId.value) {
            selectedItemName.value = `${updatedItem.first_name} ${updatedItem.last_name}`;
        }

        resetEditState();
        updateMenuItems();
    } catch (error) {
        console.error('Error updating item:', error);
    }
};

const addItem = async () => {
    if (!newItemFirstName.value.trim() || !newItemLastName.value.trim()) return;

    let existingItem = allMenuItems.value.find(item => item.normalizedName === normalizeString(`${newItemFirstName.value.trim()} ${newItemLastName.value.trim()}`));
    if (existingItem) {
        if (existingItem.deleted_at !== null) {
            try {
                const response = await api.post(`/${props.apiurl}/${existingItem.id}/restore`);
                existingItem.deleted_at = null;

                proxy.$notify(response.data.message, "", "success", "ni ni-like-2 ni-lg");

            } catch (error) {
                console.error('Error restoring item from API:', error);
            }
        }
        selectItem(existingItem);
        resetEditState();
        return;
    }

    try {
        let url = `/${props.apiurl}?search=${normalizeString(`${newItemFirstName.value.trim()} ${newItemLastName.value.trim()}`)}`;

        const response = await api.get(url);
        const apiItems = normalizeData(response.data.data);

        proxy.$notify(response.data.message, "", "success", "ni ni-like-2 ni-lg");

        existingItem = apiItems.find(item => item.normalizedName === normalizeString(`${newItemFirstName.value.trim()} ${newItemLastName.value.trim()}`));

        if (existingItem) {
            if (existingItem.deleted_at !== null) {
                await api.post(`/${props.apiurl}/${existingItem.id}/restore`);
                existingItem.deleted_at = null;
            }

            allMenuItems.value.push(existingItem);
            selectItem(existingItem);
            resetEditState();
            return;
        }
    } catch (error) {
        console.error('Error checking existing item in API:', error);
    }

    try {
        const response = await api.post(`/${props.apiurl}`, {
            first_name: newItemFirstName.value,
            last_name: newItemLastName.value,
            gender: newItemGender.value,
            whatsapp: newItemWhatsapp.value,
            birthdate: newItemBirthdate.value
        });
        const newItem = response.data.data;
        allMenuItems.value.push(normalizeData([newItem])[0]);
        proxy.$notify(response.data.message, "", "success", "ni ni-like-2 ni-lg");

        selectItem(newItem);
        resetEditState();
        updateMenuItems();
    } catch (error) {
        console.error('Error adding item:', error);
    }
};

const resetEditState = () => {
    newItemFirstName.value = '';
    newItemLastName.value = '';
    newItemGender.value = '';
    newItemWhatsapp.value = '';
    newItemBirthdate.value = '';
    isEditing.value = false;
    editingItemId.value = null;
}


const handleDelete = async (id) => {
    isDropdownOpen.value = false;
    const paciente = menuItems.value.find(item => item.id === id)
    if (paciente) {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            html: `¡No podrás revertir esto!<br>¿Deseas eliminar el paciente, <b>${paciente.name}</b>?`,
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: 'Cancelar',
            confirmButtonText: 'Sí, ¡elimínalo!',
            customClass: {
                confirmButton: 'btn bg-gradient-success',
                cancelButton: 'btn bg-gradient-danger'
            },
            buttonsStyling: false
        })

        if (result.isConfirmed) {
            deleteItem(id)
            isDropdownOpen.value = true;
        } else {
            isDropdownOpen.value = true;
        }
    }
}

const deleteItem = async (id) => {
    try {
        await api.delete(`/${props.apiurl}/${id}`);
        const index = allMenuItems.value.findIndex(item => item.id === id);
        if (index !== -1) {
            allMenuItems.value[index].deleted_at = new Date().toISOString();
            menuItems.value = menuItems.value.filter(item => item.id !== id);
        }

        if (selectedItemId.value === id) {
            selectedItemId.value = '';
            selectedItemName.value = '';
        }

        updateMenuItems();
    } catch (error) {
        console.error('Error deleting item:', error);
    }
};

const toggleDropdown = async () => {
    dropdownWidth.value = `${dropdown.value.offsetWidth}px`;
    isDropdownOpen.value = !isDropdownOpen.value;

    if (isDropdownOpen.value && allMenuItems.value.length === 0) {
        await fetchAllMenuValues();
        await nextTick();
    }
};

watch(() => proxy.$eventBus, (newValue) => {
    if (newValue.currentOpenMenu !== 'MenuSelectedPatient') {
        isDropdownOpen.value = false;
    }
});

proxy.$eventBus.on('close-other-dropdowns', (source) => {
    if (source !== 'MenuSelectedPatient') {
        isDropdownOpen.value = false;
    }
});

const closeDropdown = () => {
    isDropdownOpen.value = false;
}

const onScroll = async () => {
    if (scrollableOptions.value) {
        const { scrollTop, scrollHeight, clientHeight } = scrollableOptions.value;
        if (scrollTop + clientHeight >= scrollHeight - 10 && currentPage.value < totalPages.value) {
            currentPage.value += 1;
            await fetchAllMenuValues(currentPage.value);
        }
    }
};

onMounted(async () => {
    if (props.localData.length > 0) {
        allMenuItems.value = normalizeData(props.localData);
    } else {
        await fetchAllMenuValues();
    }

    if (props.modelValue) {
        await findSelectedItem(props.modelValue);
    }

    document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
});

watch(() => props.modelValue, async (newValue) => {
    if (newValue) {
        await findSelectedItem(newValue);
    }
}, { immediate: true });

watch(searchQuery, () => {
    currentPage.value = 1;
    allMenuItems.value = [];
    fetchAllMenuValues();
});

watch(allMenuItems, () => {
    updateMenuItems();
});

watch(() => props.modelValue, (newValue) => {
    if (!newValue) {
        selectedItemId.value = '';
        selectedItemName.value = '';
    }
}, { immediate: true });

</script>

<template>
    <div class="m-0">
        <label for="menu-selected2">{{ label }}</label>
        <div ref="dropdown" class="dropdown">
            <button
id="dropdownMenuButton"
                class="form-control dropdown-toggle d-flex justify-content-between align-items-center text-start"
                type="button" :class="{ show: isDropdownOpen }" @click="toggleDropdown">
                <span class="truncated-text">{{ selectedItemName || (selectedItemId ? '' : 'Seleccionar') }}</span>
            </button>
            <ul
class="dropdown-menu custom-dropdown-menu"
                :class="{ 'dropdown-menu-up': dropdownUp, 'dropdown-menu-left': dropdownLeft, 'dropdown-menu-right': dropdownRight, show: isDropdownOpen }"
                :style="{ width: `${dropdownWidth}px` }" aria-labelledby="dropdownMenuButton">

                <li class="px-3 fixed-header d-flex align-items-center">
                    <input
id="searchQuery" v-model="searchQuery" placeholder="Buscar nombre del paciente..."
                        class="form-control mb-2 flex-grow-1" />
                    <button class="close-button" @click.prevent="closeDropdown">
                        <i class="fas fa-times"></i>
                    </button>
                </li>

                <div ref="scrollableOptions" class="scrollable-options" @scroll="onScroll">
                    <li
v-for="item in menuItems" :key="item.id"
                        :class="{ 'selected-item': selectedItemId === item.id }"
                        class="d-flex justify-content-between align-items-center p-1 menu-item">
                        <span
class="flex-grow-1 ms-3 text-start" style="flex-shrink: 0; width: 70%;"
                            @click="selectItem(item); closeDropdown()">{{ item.last_name }} {{ item.first_name
                            }}</span>
                        <a
v-if="canEditAndAdd" class="btn-link p-1 text-primary me-0" style="cursor: pointer;"
                            @click.prevent="editItem(item)">
                            <i class="fas fa-edit"></i>
                        </a>
                        <a
v-if="canEditAndAdd" class="btn-link p-1 text-danger me-3" style="cursor: pointer;"
                            @click.prevent="handleDelete(item.id)">
                            <i class="fas fa-trash-alt"></i>
                        </a>
                    </li>
                    <li v-if="isLoadingMore" class="text-center p-2">Cargando más...</li>
                </div>

                <li v-if="canEditAndAdd" class="dropdown-item fixed-footer">
                    <div class="d-flex gap-2">
                        <div class="flex-grow-1">
                            <label for="newItemLastName">Apellidos</label>
                            <input
id="newItemLastName" v-model="newItemLastName" placeholder="Apellido"
                                class="form-control form-control-sm" />
                        </div>
                        <div class="flex-grow-1">
                            <label for="newItemFirstName">Nombres</label>
                            <input
id="newItemFirstName" v-model="newItemFirstName" placeholder="Nombre"
                                class="form-control form-control-sm" />
                        </div>
                    </div>
                    <div class="d-flex gap-2">
                        <div class="flex-grow-2">
                            <label for="newItemGender">Género</label>
                            <GenderSelect
id="gender" v-model="newItemGender" :errors="errors"
                                error-field-name="gender" />
                        </div>
                        <div class="flex-grow-1">
                            <label for="newItemBirthdate">Fecha de Nacimiento</label>
                            <DatePicker
id="newItemBirthdate" v-model="newItemBirthdate"
                                class="form-control form-control-sm" :errors="errors" error-field-name="birthdate" />
                        </div>
                        <div class="flex-grow-1">
                            <label for="newItemWhatsapp">WhatsApp</label>
                            <input
id="newItemWhatsapp" v-model="newItemWhatsapp" placeholder="WhatsApp"
                                class="form-control form-control-sm" />
                        </div>
                    </div>
                    <div class="btn-create-update">
                        <button class="btn btn-primary me-2" @click.prevent="isEditing ? updateItem() : addItem()">
                            {{ isEditing ? 'Guardar' : 'Agregar' }}
                        </button>
                        <button class="btn btn-secondary" @click.prevent="resetEditState">Cancelar</button>
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>

<style scoped>
.menu-item:hover {
    background-color: hsl(0, 0%, 95%);
}

.selected-item {
    background-color: #eeeeee;
}

.selected-item:hover {
    background-color: #eeeeee;
}

.dropdown-menu {
    width: 466px !important;
    min-width: 466px;
    display: flex;
    flex-direction: column;
    max-height: 600px;
    overflow: hidden;
    border-radius: 8px;
}

.scrollable-options {
    max-height: 150px;
    overflow-y: auto;
}

.fixed-header {
    position: sticky;
    top: 0;
    background: white;
    z-index: 100;
    border-bottom: 1px solid #ddd;
}

.fixed-header input {
    width: 100%;
    padding: 8px;
}

.fixed-footer {
    background: white;
    border-top: 1px solid #ddd;
    padding: 10px;
}

.fixed-footer .form-control {
    flex: 1 1 100%;
}

.truncated-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.btn-create-update {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
}

.form-control-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    line-height: 1.5;
    border-radius: 0.4rem;
}

.fixed-footer label {
    margin-bottom: 0px !important;
}

.fixed-header label {
    margin-top: 0px !important;
    margin-bottom: 0px !important;
}

.dropdown-item {
    background-color: white !important;
    opacity: 1 !important;
}

.custom-dropdown-menu {
    display: flex;
    flex-direction: column;
    max-height: 400px;
}

.close-button {
    background: transparent;
    border: none;
    color: #6c757d;
    font-size: 1.2rem;
    font-weight: bold;
    cursor: pointer;
    top: -2px !important;
    left: 5px !important;
    transition: color 0.3s ease, transform 0.2s ease;
    position: relative;
}

.close-button:hover {
    color: #495057;
    /* Un gris más oscuro al pasar el cursor */
    transform: scale(1.1);
    /* Hace un pequeño zoom al pasar el cursor */
}

.close-button:focus {
    outline: none;
    box-shadow: 0 0 5px rgba(108, 117, 125, 0.5);
    /* Sombra para accesibilidad */
}

.close-button i {
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
